import { Clock, Loader2, CheckCircle, XCircle, AlertTriangle, Send, Inbox, FileCheck } from 'lucide-react';

// Jednotná status pill komponenta.
// Použití:
//   <StatusBadge status="approved" />                       // preset label + tone + ikona
//   <StatusBadge status="approved" label="Custom" />        // override label
//   <StatusBadge tone="success">Vyhovuje</StatusBadge>      // manual tone + children
//   <StatusBadge tone="warning" icon={AlertTriangle}>Blíží se</StatusBadge>
//   <StatusBadge status="approved" withIcon={false} />      // bez ikony
//
// Nové status typy se přidávají do STATUS_MAP níže — callsites zůstávají beze změny.

const STATUS_MAP = {
  // Calibration status
  pending_intake: { tone: 'info', label: 'Čeká', icon: Clock },
  in_progress: { tone: 'warning', label: 'Probíhá', icon: Loader2 },
  completed: { tone: 'info', label: 'Dokončeno', icon: FileCheck },
  pending_completion: { tone: 'info', label: 'Čeká na dokončení sond', icon: Clock },
  pending_approval: { tone: 'warning', label: 'K technické kontrole', icon: Clock },
  under_technical_review: { tone: 'warning', label: 'Po technické kontrole', icon: FileCheck },
  approved: { tone: 'success', label: 'Schváleno', icon: CheckCircle },
  rejected: { tone: 'danger', label: 'Vráceno', icon: XCircle },

  // Order status
  new: { tone: 'info', label: 'Nová' },
  confirmed: { tone: 'warning', label: 'Potvrzená' },
  received: { tone: 'success', label: 'Přijatá' },

  // Result (calibration outcome)
  pass: { tone: 'success', label: 'Vyhovuje', icon: CheckCircle },
  fail: { tone: 'danger', label: 'Nevyhovuje', icon: XCircle },
  unassessed: { tone: 'neutral', label: 'Neposouzeno', icon: AlertTriangle },

  // Etalon expiry (computed)
  expired: { tone: 'danger', label: 'Expirováno', icon: AlertTriangle },
  expiring: { tone: 'warning', label: 'Blíží se', icon: AlertTriangle },
  ok: { tone: 'success', label: 'OK', icon: CheckCircle },

  // Intake sent-to-calibration
  sent: { tone: 'success', label: 'Odesláno', icon: Send },
  ready: { tone: 'warning', label: 'Připraveno', icon: Inbox },
};

const TONE_STYLES = {
  success: { bg: 'var(--ct-success-soft)', fg: 'var(--ct-success)' },
  warning: { bg: 'var(--ct-warning-soft)', fg: 'var(--ct-warning)' },
  danger: { bg: 'var(--ct-error-soft)', fg: 'var(--ct-error)' },
  info: { bg: 'var(--ct-info-soft)', fg: 'var(--ct-info)' },
  neutral: { bg: 'var(--ct-bg-secondary)', fg: 'var(--ct-text-secondary)' },
};

export default function StatusBadge({
  status,
  tone,
  label,
  icon,
  withIcon = true,
  size = 'sm',
  className = '',
  children,
}) {
  const preset = status ? STATUS_MAP[status] : null;
  const effectiveTone = tone || preset?.tone || 'neutral';
  const effectiveLabel = children ?? label ?? preset?.label ?? status ?? '—';
  const IconComp = icon !== undefined ? icon : (withIcon ? preset?.icon : null);
  const styles = TONE_STYLES[effectiveTone] || TONE_STYLES.neutral;

  const sizing = size === 'md'
    ? 'px-2.5 py-1 text-xs gap-1.5'
    : 'px-2.5 py-0.5 text-[11px] gap-1';
  const iconSize = size === 'md' ? 'w-3.5 h-3.5' : 'w-3.5 h-3.5';

  return (
    <span
      className={`inline-flex items-center rounded-full font-semibold whitespace-nowrap ${sizing} ${className}`}
      style={{ background: styles.bg, color: styles.fg }}
    >
      {IconComp && <IconComp className={`${iconSize} flex-shrink-0`} />}
      <span>{effectiveLabel}</span>
    </span>
  );
}
